import React, {createContext, useContext, useReducer, useEffect, useRef, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {LoadingLogo} from "../layout/LoadingLogo";
import {LogoPlacer} from "../layout/LogoPlacer";
import {
  undoRedoReducer,
  createInitialHistoryState,
  SET_STORY,
  UNDO,
  REDO,
  SYNC_EXTERNAL_STORY,
} from "./undoRedoReducer";

const ScrollytellPropsContext = createContext(undefined);
const ScrollytellStateContext = createContext(undefined);
const ScrollytellDispatchContext = createContext(undefined);
const ScrollytellTranslateContext = createContext(undefined);

export const SET_ACTIVE_ELEMENT = "setActiveElement";
export const SET_SHOW_GRID_VIEW = "setShowGridView";
export const SET_EDIT_DIRECTIONS = 'setEditDirections';
export const SET_A11Y_DRAWER_OPEN = "setA11yDrawerOpen";

const initialState = {
  activeElement: [undefined, undefined, undefined],
  showGrid: false,
  editDirections: false,
  a11yDrawerOpen: false,
};

const scrollytellReducer = (state, action) => {
  switch (action.type) {
    case SET_ACTIVE_ELEMENT: {
      return {
        ...state,
        activeElement: [
          action.activeSection,
          action.activeContainer,
          action.activeBlock,
        ],
      };
    }
    case SET_SHOW_GRID_VIEW: {
      return {
        ...state,
        showGrid: action.showGrid,
      };
    }
    case SET_EDIT_DIRECTIONS:
      return {
        ...state,
        editDirections: action.editDirections
      };
    case SET_A11Y_DRAWER_OPEN:
      return {
        ...state,
        a11yDrawerOpen: action.open,
      };
    default:
      throw new Error(`Unhandled action type ${action.type}`);
  }
};

export const ScrollytellProvider = ({
  story,
  onChange,
  onMediaSelect,
  onTriggerSettings,
  children,
}) => {
  const [state, dispatch] = useReducer(
    scrollytellReducer,
    { ...initialState },
    undefined
  );

  const [historyState, historyDispatch] = useReducer(
    undoRedoReducer,
    story,
    createInitialHistoryState
  );

  const { t, ready } = useTranslation("vm-scrolly.app", {
    useSuspense: false,
  });

  const prevStoryPropRef = useRef(story);

  useEffect(() => {
    if (
      story &&
      story !== prevStoryPropRef.current &&
      story !== historyState.present
    ) {
      prevStoryPropRef.current = story;
      historyDispatch({ type: SYNC_EXTERNAL_STORY, payload: story });
    }
  }, [story, historyState.present]);

  const handleChange = (nextStory, options = {}) => {
    historyDispatch({
      type: SET_STORY,
      payload: nextStory,
      skipHistory: options.skipHistory,
    });
    if (onChange) {
      onChange(nextStory);
    }
  };

  const undo = useCallback(() => {
    if (historyState.canUndo) {
      const previous = historyState.past[historyState.past.length - 1];
      historyDispatch({ type: UNDO });
      if (onChange && previous) {
        onChange(previous);
      }
    }
  }, [historyState.canUndo, historyState.past, onChange]);

  const redo = useCallback(() => {
    if (historyState.canRedo) {
      const next = historyState.future[0];
      historyDispatch({ type: REDO });
      if (onChange && next) {
        onChange(next);
      }
    }
  }, [historyState.canRedo, historyState.future, onChange]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        (target.closest && target.closest(".public-DraftEditor-content"));

      if (isInput) return;

      const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      if (modKey && !e.shiftKey && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        undo();
      } else if (
        (modKey && (e.key === "y" || e.key === "Y")) ||
        (modKey && e.shiftKey && (e.key === "z" || e.key === "Z"))
      ) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [historyState, onChange, redo, undo]);

  if (!ready) {
    return (
        <LogoPlacer>
          <LoadingLogo />
        </LogoPlacer>
    )
  } else {
    return (
      <ScrollytellPropsContext.Provider
        value={{
          onChange: handleChange,
          story: historyState.present || story,
          undo,
          redo,
          canUndo: historyState.canUndo,
          canRedo: historyState.canRedo,
          onMediaSelect,
          onTriggerSettings,
        }}
      >
        <ScrollytellStateContext.Provider value={state}>
          <ScrollytellDispatchContext.Provider value={dispatch}>
            <ScrollytellTranslateContext.Provider value={t}>
              {children}
            </ScrollytellTranslateContext.Provider>
          </ScrollytellDispatchContext.Provider>
        </ScrollytellStateContext.Provider>
      </ScrollytellPropsContext.Provider>
    );
  }
};

export const useScrollyTellProps = () => {
  const context = useContext(ScrollytellPropsContext);

  if (undefined === context) {
    return { story: {} };
  } else {
    return context;
  }
};

export const useScrollytellState = () => {
  const context = useContext(ScrollytellStateContext);

  if (undefined === context) {
    return {
      activeElement: [null, null, null],
      showGrid: false,
      editDirections: true,
      a11yDrawerOpen: false,
    };
  } else {
    return context;
  }
};

export const useScrollytellDispatch = () => {
  const context = useContext(ScrollytellDispatchContext);

  if (undefined === context) {
    return () => {};
  } else {
    return context;
  }
};

export const useScrollytellTranslation = () => {
  const context = useContext(ScrollytellTranslateContext);

  if (undefined === context) {
    return (key, fallback, options) => {
      let str = fallback || key;
      if (options && typeof options === "object" && typeof str === "string") {
        Object.entries(options).forEach(([k, v]) => {
          str = str.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), String(v));
        });
      }
      return str;
    };
  } else {
    return context;
  }
};
